// /aabout2 drawn as a real 3D scene, in WebGL 2 (no library: the scene is a
// handful of flat cards).
//
// - one perspective camera on the centre line looking straight ahead; units
//   are screen pixels, the deck's plane at the camera's resting distance is
//   seen at its own size (src/about/motion.ts, scene())
// - each card a rounded rectangle (its corners cut in the shader), its two
//   sides textures: the photo (fitted to the card as object-fit: cover) or a
//   colour with a capability's name and line, drawn on a 2D canvas
// - drawn back to front: in layer order while the deck is stacked, then by
//   distance to the camera (no depth buffer: no cards cutting into each other)
// - a black layer over a card instead of transparency (nothing shows through)
// - motion blur: sideways per card from its speed on screen (desktop, as the
//   cards on the home), and from the camera's own speed, streaks out from the
//   centre (rendered to a texture, then blurred towards the centre)

import type { Scene } from "./motion"

export type Face = { color: string; ink: string; title?: string; text?: string; low?: boolean }
export type CardArt = { front: Face; back: Face; photo?: string }

const CARD_VS = `#version 300 es
in vec2 aPos;
uniform mat4 uMVP;
out vec2 vUv;
void main() {
    vUv = aPos + 0.5;
    gl_Position = uMVP * vec4(aPos, 0.0, 1.0);
}`

const CARD_FS = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uFront;
uniform sampler2D uBack;
uniform vec2 uSize;
uniform float uR;
uniform float uPhoto;      // the front is the photo
uniform float uPhotoIn;    // its fade in, 0..1
uniform vec4 uCover;       // photo uv: scale (xy), offset (zw)
uniform vec2 uMirror;      // the back's texture mirrored (x for a turn over the vertical axis, y for the horizontal)
uniform float uDark;
uniform float uBlur;       // sideways motion blur, in card widths
float edge(vec2 uv) {
    vec2 p = (uv - 0.5) * uSize;
    vec2 q = abs(p) - (uSize * 0.5 - uR);
    return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - uR;
}
vec4 face(vec2 uv, float aa) {
    float m = 1.0 - smoothstep(-aa, aa, edge(uv));
    vec3 c;
    if (gl_FrontFacing) {
        if (uPhoto > 0.5) {
            vec2 pu = uv * uCover.xy + uCover.zw;
            c = texture(uFront, pu).rgb * uPhotoIn;
        } else c = texture(uFront, uv).rgb;
    } else {
        vec2 bu = mix(uv, 1.0 - uv, uMirror);
        c = texture(uBack, bu).rgb;
    }
    return vec4(c * m, m);
}
void main() {
    float aa = max(fwidth(edge(vUv)), 0.0001);
    vec4 acc = vec4(0.0);
    if (uBlur > 0.0005) {
        for (int i = 0; i < 16; i++) {
            float f = float(i) / 15.0 - 0.5;
            acc += face(vUv + vec2(uBlur * f, 0.0), aa);
        }
        acc /= 16.0;
    } else acc = face(vUv, aa);
    acc.rgb *= 1.0 - uDark;
    outColor = acc;
}`

const POST_VS = `#version 300 es
in vec2 aPos;
out vec2 vUv;
void main() {
    vUv = aPos + 0.5;
    gl_Position = vec4(aPos * 2.0, 0.0, 1.0);
}`

const POST_FS = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
uniform sampler2D uTex;
uniform float uAmount;   // how far each pixel is smeared towards the centre (share of its distance)
void main() {
    vec2 d = vUv - 0.5;
    vec4 c = vec4(0.0);
    for (int i = 0; i < 32; i++) {
        float f = float(i) / 31.0;
        c += texture(uTex, 0.5 + d * (1.0 - uAmount * f));
    }
    outColor = c / 32.0;
}`

type M4 = Float32Array
const mul = (a: M4, b: M4): M4 => {
    const o = new Float32Array(16)
    for (let c = 0; c < 4; c++)
        for (let r = 0; r < 4; r++) {
            let s = 0
            for (let k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k]
            o[c * 4 + r] = s
        }
    return o
}
const ident = (): M4 => new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1])
const translate = (x: number, y: number, z: number): M4 => {
    const m = ident()
    m[12] = x
    m[13] = y
    m[14] = z
    return m
}
const scaleM = (x: number, y: number, z: number): M4 => {
    const m = ident()
    m[0] = x
    m[5] = y
    m[10] = z
    return m
}
const rotY = (a: number): M4 => {
    const c = Math.cos(a),
        s = Math.sin(a)
    return new Float32Array([c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1])
}
const rotX = (a: number): M4 => {
    const c = Math.cos(a),
        s = Math.sin(a)
    return new Float32Array([1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1])
}
const perspective = (fovy: number, aspect: number, near: number, far: number): M4 => {
    const f = 1 / Math.tan(fovy / 2),
        nf = 1 / (near - far)
    return new Float32Array([f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0])
}
const apply = (m: M4, x: number, y: number, z: number) => {
    const w = m[3] * x + m[7] * y + m[11] * z + m[15]
    return [(m[0] * x + m[4] * y + m[8] * z + m[12]) / w, (m[1] * x + m[5] * y + m[9] * z + m[13]) / w, w]
}

/** A face's texture: its colour, and a capability (name and line) as on the page. */
function drawFace(face: Face, w: number, h: number, s: number, quality: number, alignRight: boolean) {
    const cv = document.createElement("canvas")
    cv.width = Math.max(2, Math.round(w * quality))
    cv.height = Math.max(2, Math.round(h * quality))
    const g = cv.getContext("2d")!
    g.scale(quality, quality)
    g.fillStyle = face.color
    g.fillRect(0, 0, w, h)
    if (face.title) {
        const pad = 22 * s
        const size = 40 * s,
            lh = size * 0.86
        const body = 14 * s,
            blh = body * 1.25
        g.fillStyle = face.ink
        g.textBaseline = "alphabetic"
        const title = face.title.toUpperCase().split("\n")
        g.font = `${body}px "Unica77 LL Regular", "Unica77 LL Regular Placeholder", sans-serif`
        // the line, wrapped to the card
        const words = (face.text || "").split(" ")
        const lines: string[] = []
        let line = ""
        for (const word of words) {
            const next = line ? line + " " + word : word
            if (g.measureText(next).width > w - 2 * pad && line) {
                lines.push(line)
                line = word
            } else line = next
        }
        if (line) lines.push(line)
        const height = title.length * lh + 12 * s + lines.length * blh
        let y = face.low ? h - pad - height : pad
        const x = alignRight ? w - pad : pad
        g.textAlign = alignRight ? "right" : "left"
        g.font = `800 ${size}px "Blubird Extra Bold Display", "Blubird Extra Bold Display Placeholder", sans-serif`
        for (const t of title) {
            y += lh
            g.fillText(t, x, y - lh * 0.12)
        }
        y += 12 * s
        g.font = `${body}px "Unica77 LL Regular", "Unica77 LL Regular Placeholder", sans-serif`
        g.globalAlpha = 0.8
        for (const t of lines) {
            y += blh
            g.fillText(t, x, y - blh * 0.22)
        }
        g.globalAlpha = 1
    }
    return cv
}

export class DeckGL {
    private gl: WebGL2RenderingContext
    private card: { prog: WebGLProgram; u: Record<string, WebGLUniformLocation | null> }
    private post: { prog: WebGLProgram; u: Record<string, WebGLUniformLocation | null> }
    private quad: WebGLBuffer
    private vao: WebGLVertexArrayObject
    private tex: { front: WebGLTexture; back: WebGLTexture }[] = []
    private photo: { tex: WebGLTexture; w: number; h: number; at: number } | null = null
    private fbo: { fb: WebGLFramebuffer; tex: WebGLTexture; w: number; h: number } | null = null
    private last: Scene | null = null
    private raf = 0
    private size = { w: 0, h: 0, dpr: 1 }
    private artKey = ""
    private speeds: { x: number; speed: number }[] = []
    private cam = { z: NaN, speed: 0 }
    private lastTime = 0

    static create(canvas: HTMLCanvasElement, art: CardArt[], sideways: () => boolean) {
        const gl = canvas.getContext("webgl2", { antialias: true, alpha: false, premultipliedAlpha: true })
        if (!gl) return null
        try {
            return new DeckGL(canvas, gl, art, sideways)
        } catch {
            return null
        }
    }

    private constructor(
        private canvas: HTMLCanvasElement,
        gl: WebGL2RenderingContext,
        private art: CardArt[],
        private sideways: () => boolean
    ) {
        this.gl = gl
        this.card = this.program(CARD_VS, CARD_FS, ["uMVP", "uFront", "uBack", "uSize", "uR", "uPhoto", "uPhotoIn", "uCover", "uMirror", "uDark", "uBlur"])
        this.post = this.program(POST_VS, POST_FS, ["uTex", "uAmount"])
        this.quad = gl.createBuffer()!
        gl.bindBuffer(gl.ARRAY_BUFFER, this.quad)
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-0.5, -0.5, 0.5, -0.5, -0.5, 0.5, 0.5, 0.5]), gl.STATIC_DRAW)
        this.vao = gl.createVertexArray()!
        gl.bindVertexArray(this.vao)
        gl.enableVertexAttribArray(0)
        gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
        art.forEach(() => this.tex.push({ front: this.texture(), back: this.texture() }))
        this.speeds = art.map(() => ({ x: NaN, speed: 0 }))
        const photo = art.find((a) => a.photo)?.photo
        if (photo) this.loadPhoto(photo)
        // the faces again once the fonts are there
        ;(document as any).fonts?.ready?.then(() => {
            this.artKey = ""
            this.kick()
        })
    }

    private program(vs: string, fs: string, names: string[]) {
        const gl = this.gl
        const sh = (type: number, src: string) => {
            const s = gl.createShader(type)!
            gl.shaderSource(s, src)
            gl.compileShader(s)
            if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || "shader")
            return s
        }
        const p = gl.createProgram()!
        gl.attachShader(p, sh(gl.VERTEX_SHADER, vs))
        gl.attachShader(p, sh(gl.FRAGMENT_SHADER, fs))
        gl.bindAttribLocation(p, 0, "aPos")
        gl.linkProgram(p)
        if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) || "link")
        const u: Record<string, WebGLUniformLocation | null> = {}
        for (const n of names) u[n] = gl.getUniformLocation(p, n)
        return { prog: p, u }
    }

    private texture() {
        const gl = this.gl
        const t = gl.createTexture()!
        gl.bindTexture(gl.TEXTURE_2D, t)
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 255]))
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
        return t
    }

    private upload(t: WebGLTexture, src: TexImageSource) {
        const gl = this.gl
        gl.bindTexture(gl.TEXTURE_2D, t)
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true)
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src)
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false)
        gl.generateMipmap(gl.TEXTURE_2D)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR)
    }

    private loadPhoto(src: string) {
        const img = new Image()
        img.decoding = "async"
        // the size the screen needs (as the page's srcset)
        const want = innerWidth * Math.min(2, devicePixelRatio || 1)
        const w = want <= 1200 ? 1200 : want <= 2000 ? 2000 : 3364
        img.src = src.replace("3364", String(w))
        const done = () => {
            const tex = this.texture()
            this.upload(tex, img)
            this.photo = { tex, w: img.naturalWidth, h: img.naturalHeight, at: performance.now() }
            this.kick()
        }
        ;(img.decode ? img.decode() : new Promise((r) => (img.onload = r))).then(done, () => (img.onload = done))
    }

    /** the cards' faces, drawn again when their size changes or the fonts arrive */
    private faces(scene: Scene) {
        const c = scene.cards[1] || scene.cards[0]
        const key = `${Math.round(c.w)}x${Math.round(c.h)}:${scene.axis}:${(document as any).fonts?.status}`
        if (key === this.artKey) return
        this.artKey = key
        const quality = Math.min(3, 2048 / Math.max(1, c.w)) * 1
        const right = scene.axis === "x"
        this.art.forEach((a, i) => {
            if (!a.photo) this.upload(this.tex[i].front, drawFace(a.front, c.w, c.h, scene.scale, quality, right))
            this.upload(this.tex[i].back, drawFace(a.back, c.w, c.h, scene.scale, quality, false))
        })
    }

    /** a new moment to show */
    show(scene: Scene) {
        this.last = scene
        this.kick()
    }

    private kick() {
        if (!this.raf) this.raf = requestAnimationFrame((now) => this.render(now))
    }

    private resize() {
        const dpr = Math.min(innerWidth < 700 ? 1.75 : 2, devicePixelRatio || 1)
        const w = Math.round(innerWidth * dpr),
            h = Math.round(innerHeight * dpr)
        if (w !== this.size.w || h !== this.size.h) {
            this.canvas.width = w
            this.canvas.height = h
            this.size = { w, h, dpr }
        }
    }

    private target(w: number, h: number) {
        const gl = this.gl
        if (this.fbo && this.fbo.w === w && this.fbo.h === h) return this.fbo
        if (this.fbo) {
            gl.deleteFramebuffer(this.fbo.fb)
            gl.deleteTexture(this.fbo.tex)
        }
        const tex = gl.createTexture()!
        gl.bindTexture(gl.TEXTURE_2D, tex)
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
        const fb = gl.createFramebuffer()!
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb)
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0)
        this.fbo = { fb, tex, w, h }
        return this.fbo
    }

    private render(now: number) {
        this.raf = 0
        const sc = this.last
        if (!sc) return
        const gl = this.gl
        this.resize()
        this.faces(sc)
        const dt = this.lastTime ? Math.min(0.064, (now - this.lastTime) / 1000) : 1 / 60
        this.lastTime = now
        const vw = innerWidth,
            vh = innerHeight
        const D = sc.distance
        // the camera: on the centre line, at sc.camera in front of the deck's plane
        const proj = perspective(2 * Math.atan(vh / 2 / D), vw / vh, Math.max(1, D * 0.02), D * 40)
        const view = translate(0, 0, -sc.camera)
        const vp = mul(proj, view)
        // its speed (share of its distance per second, smoothed): the streaks
        const cz = sc.camera
        const v = !isNaN(this.cam.z) && dt > 0 ? Math.abs(cz - this.cam.z) / D / dt : 0
        this.cam.z = cz
        this.cam.speed += (v - this.cam.speed) * (1 - Math.exp(-dt * 10))
        if (this.cam.speed < 0.002 && v === 0) this.cam.speed = 0
        const streak = Math.min(0.22, this.cam.speed * 0.09)
        let moving = streak > 0.001

        // the cards: back to front
        const side = sc.axis === "x"
        const list = sc.cards
            .map((c, i) => ({ c, i, dist: Math.hypot(side ? c.x : 0, side ? 0 : c.y, sc.camera) }))
            .filter((o) => o.c.visible)
            .sort((a, b) => (sc.depth ? b.dist - a.dist : a.c.layer - b.c.layer))

        const fb = streak > 0.001 ? this.target(this.size.w, this.size.h) : null
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb ? fb.fb : null)
        gl.viewport(0, 0, this.size.w, this.size.h)
        gl.clearColor(0, 0, 0, 1)
        gl.clear(gl.COLOR_BUFFER_BIT)
        gl.disable(gl.DEPTH_TEST)
        gl.disable(gl.CULL_FACE)
        gl.enable(gl.BLEND)
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
        gl.useProgram(this.card.prog)
        gl.bindVertexArray(this.vao)
        const u = this.card.u
        gl.uniform1i(u.uFront, 0)
        gl.uniform1i(u.uBack, 1)
        gl.uniform2f(u.uMirror, side ? 1 : 0, side ? 0 : 1)
        const photoIn = this.photo ? Math.min(1, (now - this.photo.at) / 1600) : 0
        // (as the page: cubic-bezier(.25, 0, 0, 1), from 112% to its size)
        const ease = (x: number) => {
            let lo = 0,
                hi = 1
            for (let i = 0; i < 20; i++) {
                const m = (lo + hi) / 2,
                    bx = 3 * (1 - m) * (1 - m) * m * 0.25 + m * m * m
                if (bx < x) lo = m
                else hi = m
            }
            const m = (lo + hi) / 2
            return 3 * (1 - m) * m * m + m * m * m
        }
        const pin = ease(photoIn)
        if (this.photo && photoIn < 1) moving = true
        for (const { c, i } of list) {
            const a = (c.angle * Math.PI) / 180
            // (CSS turned it rotateY(-angle) / rotateX(angle) with y down; here y is up)
            const turn = side ? rotY(-a) : rotX(-a)
            const model = mul(mul(translate(c.x, -c.y, 0), turn), scaleM(c.w * c.scale, c.h * c.scale, 1))
            const mvp = mul(vp, model)
            gl.uniformMatrix4fv(u.uMVP, false, mvp)
            gl.uniform2f(u.uSize, c.w, c.h)
            gl.uniform1f(u.uR, c.r)
            gl.uniform1f(u.uDark, c.dark)
            const art = this.art[i]
            const isPhoto = !!art.photo
            gl.uniform1f(u.uPhoto, isPhoto ? 1 : 0)
            gl.activeTexture(gl.TEXTURE0)
            gl.bindTexture(gl.TEXTURE_2D, isPhoto ? (this.photo?.tex ?? this.tex[i].front) : this.tex[i].front)
            gl.activeTexture(gl.TEXTURE1)
            gl.bindTexture(gl.TEXTURE_2D, this.tex[i].back)
            if (isPhoto) {
                gl.uniform1f(u.uPhotoIn, this.photo ? pin : 0)
                // object-fit: cover, centred, and the fade-in's 112% -> 100%
                const ai = this.photo ? this.photo.w / this.photo.h : 1.5,
                    ac = c.w / c.h
                let sx = 1,
                    sy = 1
                if (ac > ai) sy = ai / ac
                else sx = ac / ai
                const zoom = 1.12 - 0.12 * pin
                sx /= zoom
                sy /= zoom
                gl.uniform4f(u.uCover, sx, sy, 0.5 - sx / 2, 0.5 - sy / 2)
            }
            // sideways motion blur from its speed on screen (desktop)
            const sp = this.speeds[i]
            const [px] = apply(mvp, 0, 0, 0)
            const sx = ((px + 1) / 2) * vw
            const speed = side && !isNaN(sp.x) && dt > 0 ? Math.abs(sx - sp.x) / dt : 0
            sp.x = sx
            sp.speed += (Math.min(speed * 0.012, 18) - sp.speed) * (1 - Math.exp(-dt * 12))
            if (sp.speed < 0.3 && speed === 0) sp.speed = 0
            if (sp.speed > 0.3) moving = true
            const [lx] = apply(mvp, -0.5, 0, 0),
                [rx] = apply(mvp, 0.5, 0, 0)
            const onScreen = (Math.abs(rx - lx) / 2) * vw
            gl.uniform1f(u.uBlur, side && sp.speed > 0.3 ? Math.min(0.5, (sp.speed * 3) / Math.max(20, onScreen)) : 0)
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
        }

        // the camera's own motion blur: streaks out from the centre
        if (fb) {
            gl.bindFramebuffer(gl.FRAMEBUFFER, null)
            gl.viewport(0, 0, this.size.w, this.size.h)
            gl.disable(gl.BLEND)
            gl.useProgram(this.post.prog)
            gl.activeTexture(gl.TEXTURE0)
            gl.bindTexture(gl.TEXTURE_2D, fb.tex)
            gl.uniform1i(this.post.u.uTex, 0)
            gl.uniform1f(this.post.u.uAmount, streak)
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
        }
        // keep drawing while something is still settling (blur, the photo coming in)
        if (moving) this.kick()
        else this.lastTime = 0
    }

    dispose() {
        cancelAnimationFrame(this.raf)
        const lose = this.gl.getExtension("WEBGL_lose_context")
        lose?.loseContext()
    }
}
